import styled from 'styled-components';

// Generic Glass Container (Optimized for TV)
export const GlassContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.theme.colors?.glassBackground || 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: ${(props) => props.theme.effects?.backdropBlur || 'blur(4px)'};
  overflow: hidden;
  /* TV Performance Optimizations */
  transform: translateZ(0);
  will-change: auto;
`;

// Generic Glass Card (Optimized for TV)
export const GlassCard = styled.div`
  position: relative;
  width: 90%;
  max-width: ${(props) => props.maxWidth || '800px'};
  background: ${(props) => props.theme.colors?.glassBackground || 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: ${(props) => props.theme.effects?.backdropBlurLight || 'blur(2px)'};
  border-radius: ${(props) => props.theme.effects?.borderRadiusLarge || '16px'};
      /* box-shadow removed for TV performance */
  padding: ${(props) => props.padding || '40px'};
  margin: ${(props) => props.margin || '20px'};
  margin-top: 120px;
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'};
  will-change: transform;
  
  /* Simplified Hover Effects for TV */
  &:hover {
    transform: translateY(-2px);
  }
`;

// Generic Header (Optimized for TV)
export const Header = styled.header`
  text-align: center;
  font-weight: bold;
  left: 0;
  right: 0;
  top: 50px;
  position: absolute;
  z-index: 1;
  font-size: large;
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};

  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'};
  padding: 15px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  
  h1 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
    /* text-shadow removed for TV performance */
  }
`;

// Generic Grid Layout
export const Grid = styled.div`
  display: grid;
  grid-template-columns: ${(props) => props.columns || 'repeat(auto-fit, minmax(300px, 1fr))'};
  gap: ${(props) => props.gap || '30px'};
  width: 100%;
`;

// Generic Interactive Item (Optimized for TV)
export const InteractiveItem = styled.div`
  position: relative;
  background: ${(props) => (props.active 
    ? props.theme.colors?.buttonGradient || props.theme.colors?.buttonPrimary
    : props.theme.colors?.glassBackground || props.theme.colors?.buttonSecondary)};
  backdrop-filter: ${(props) => props.theme.effects?.backdropBlurLight || 'blur(2px)'};
  border-radius: ${(props) => props.theme.effects?.borderRadiusMedium || '12px'};
  padding: ${(props) => props.padding || '30px'};
  outline: none !important;
  cursor: pointer;
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'};
  /* box-shadow removed for TV performance */
  transform: ${(props) => (props.active ? 'scale(1.02) translateY(-2px)' : 'scale(1) translateY(0)')};
  overflow: hidden;
  will-change: transform, background;
  
  /* Simplified Hover Effects for TV */
  &:hover {
    transform: ${(props) => (props.active ? 'scale(1.02) translateY(-2px)' : 'scale(1.01) translateY(-1px)')};
    /* box-shadow removed for TV performance */
    z-index: ${(props) => (props.active ? '4' : '2')};
  }
  
  /* Simplified Active Effects for TV */
  &:active {
    transform: ${(props) => (props.active ? 'scale(1.01) translateY(-1px)' : 'scale(0.99) translateY(0)')};
    /* box-shadow removed for TV performance */
    transition: ${(props) => props.theme.effects?.transitionFast || 'all 0.15s ease-out'};
  }
`;

// Generic Item Content
export const ItemContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(props) => props.gap || '15px'};
  
  i {
    font-size: ${(props) => props.iconSize || '3rem'};
    color: ${(props) => props.theme.colors.textPrimary};
    /* text-shadow removed for TV performance */
    transition: ${(props) => props.theme.effects.transitionMedium}; /* Simplified for TV performance */
  }
  
  h3 {
    font-size: ${(props) => props.titleSize || '1.4rem'};
    font-weight: 600;
    color: ${(props) => props.theme.colors.textPrimary};
    /* text-shadow removed for TV performance */
    text-align: center;
    margin: 0;
    background: ${(props) => props.theme.colors.glassBackground};
    -webkit-background-clip: text;
  
    background-clip: text;
  }
  
  p {
    font-size: ${(props) => props.descriptionSize || '0.9rem'};
    color: ${(props) => props.theme.colors.textPrimary};
    /* text-shadow removed for TV performance */
    text-align: center;
    margin: 0;
    opacity: 0.8;
  }
`;

// Generic Button
export const Button = styled.button`
  position: relative;
  background: ${(props) => (props.active 
    ? props.theme.colors.buttonPrimary
    : props.theme.colors.glassBackground)};
  backdrop-filter: ${(props) => props.theme.effects.backdropBlurLight};
  border-radius: ${(props) => props.theme.effects.borderRadiusMedium};
  padding: ${(props) => props.padding || '12px 24px'};
  cursor: pointer;
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'};
  /* box-shadow removed for TV performance */
  transform: ${(props) => (props.active ? 'scale(1.02) translateY(-2px)' : 'scale(1) translateY(0)')};
  overflow: hidden;
  will-change: transform, background;
  font-size: ${(props) => props.fontSize || '1rem'};
  font-weight: 600;
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
  /* text-shadow removed for TV performance */
  
  /* Glow Effect - REMOVED for TV performance */
  /* &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${(props) => (props.active 
      ? props.theme.colors?.glowGradient || 'linear-gradient(90deg, rgba(255, 255, 255, 0.6), transparent)'
      : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)')};
    border-radius: ${(props) => props.theme.effects?.borderRadiusMedium || '16px'} ${(props) => props.theme.effects?.borderRadiusMedium || '16px'} 0 0;
    transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV performance */
  } */
  
  /* Glass Overlay - REMOVED for TV performance */
  /* &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) => props.theme.colors?.glassBackground || 'linear-gradient(135deg, rgba(255, 255, 255, 0.05), transparent)'};
    pointer-events: none;
    transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV performance */
  } */
  
  /* Hover Effects - Simplified for TV */
  &:hover {
    /* border-color: ${(props) => props.theme.colors?.glassBorder || 'rgba(255, 255, 255, 0.3)'}; REMOVED for TV performance */
    transform: ${(props) => (props.active ? 'scale(1.03) translateY(-3px)' : 'scale(1.01) translateY(-1px)')};
    /* box-shadow removed for TV performance */
    z-index: ${(props) => (props.active ? '4' : '2')};
  }
  
  /* Active Effects - Optimized for TV */
  &:active {
    transform: ${(props) => (props.active ? 'scale(1.01) translateY(-1px)' : 'scale(0.98) translateY(0)')}; /* Reduced for TV performance */
    /* box-shadow removed for TV performance */
    transition: ${(props) => props.theme.effects?.transitionFast || 'all 0.1s ease-out'}; /* Simplified for TV */
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// Generic Modal Overlay - Optimized for TV
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV */
`;

// Generic Modal Content - Optimized for TV
export const ModalContent = styled.div`
  position: relative;
  background: ${(props) => props.theme.colors?.glassBackground || 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: ${(props) => props.theme.effects?.backdropBlurLight || 'blur(2px)'}; /* Reduced for TV performance */
  /* border: ${(props) => props.theme.colors?.glassBorder || '1px solid rgba(255, 255, 255, 0.2)'}; REMOVED for TV performance */
  border-radius: ${(props) => props.theme.effects?.borderRadiusLarge || '20px'}; /* Reduced from 24px */
  /* box-shadow removed for TV performance */
  padding: ${(props) => props.padding || '40px'};
  max-width: ${(props) => props.maxWidth || '600px'};
  width: ${(props) => props.width || '90%'};
  max-height: ${(props) => props.maxHeight || '80vh'};
  overflow-y: auto;
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV */
`;

// Generic Display Container (replaces ContainerDisplay)
export const DisplayContainer = styled.div`
  background-color: ${(props) => props.theme.colors.containerBackgroundSecondary};
  color: #000;
  position: absolute;
  overflow: hidden;
  padding: 20px;
  text-align: center;
  border-radius: 12px;
  /* box-shadow removed for TV performance */
  font-size: 1.1rem;
  max-width: 90%;
  max-height: 80%;
  margin: auto;
  top: 20px;
  left: 0;
  right: 0;
`;

// Generic White Background Row Item - Optimized for TV
export const WhiteBackgroundRowItem = styled.div`
  background: ${(props) => props.theme.colors?.glassBackground || 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: ${(props) => props.theme.effects?.backdropBlur || 'blur(4px)'};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${(props) => props.theme.effects?.borderRadiusLarge || '16px'};
  padding: 30px;
  margin: 10px;
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
  /* box-shadow removed for TV performance */
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'};
  position: relative;
  overflow: hidden;
  will-change: transform;

  /* Clock component styling - Optimized for TV */
  .react-clock {
    background: ${(props) => props.theme.colors?.glassBackground || 'rgba(255, 255, 255, 0.15)'};
    border-radius: ${(props) => props.theme.effects?.borderRadiusMedium || '12px'};
    padding: 15px;
    backdrop-filter: ${(props) => props.theme.effects?.backdropBlurLight || 'blur(2px)'};
    border: 1px solid rgba(255, 255, 255, 0.2);
    /* box-shadow removed for TV performance */
  }

  .react-clock__face {
    background: ${(props) => props.theme.colors.glassBackground};
    border-radius: 50%;
  }

  .react-clock__mark {
    background: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
    /* box-shadow removed for TV performance */
  }

  .react-clock__mark__body {
    background: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
  }

  .react-clock__hand {
    background: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
    /* box-shadow removed for TV performance */
  }

  .react-clock__hand__body {
    background: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
  }

  .react-clock__number {
    color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
    /* text-shadow removed for TV performance */
    font-weight: 600;
    font-size: 14px;
  }

  /* Text content styling for better readability */
  > div {
    color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
    /* text-shadow removed for TV performance */
    font-weight: 500;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) => props.theme.colors?.glowGradient || 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), transparent)'};
    border-radius: ${(props) => props.theme.effects?.borderRadiusLarge || '20px'};
    opacity: 0;
    transition: ${(props) => props.theme.effects?.transitionFast || 'all 0.2s ease'};
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-2px); /* Reduced from -5px for TV performance */
    
    &::before {
      opacity: 1;
    }

    .react-clock {
      /* box-shadow removed for TV performance */
    }
  }

  &:active {
    transform: translateY(-2px);
    /* box-shadow removed for TV performance */
  }
`;

// Generic Row Container
export const RowContainer = styled.div`
  justify-content: center;
  display: inline-flex;
  flex-direction: row;
  margin: 0 auto;
  padding: 7px;
  height: fit-content;
`;

// Generic Row Item
export const RowItem = styled.div`
  background-color: ${(props) => props.theme.colors.containerBackgroundSecondary};
  padding: 30px;
  margin: 10px;
  border-radius: 20px;
`;

// Generic Items Container
export const ItemsContainer = styled.div`
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 20px;
`;

// Generic Items Scroll Container
export const ItemsScrollContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  padding: 15px;
`;

// Generic Item
export const Item = styled.div`
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 50px;
  padding: 0 10px;
  margin: 5px 0;
  background-color: ${(props) => props.theme.colors?.background};
  border-radius: 0.25em;
  /* box-shadow removed for TV performance */
  cursor: pointer;

  .item-title {
    flex-grow: 1;
    margin-right: 20px;
  }

  &.active {
    background-color: ${(props) => props.theme.colors?.background};
    color: ${(props) => props.theme.colors?.buttonPrimary};
  }
`;

// Generic Info Wrapper
export const InfoWrapper = styled.div`
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 50px;
  padding: 0 10px;
  margin: 5px 0;
  background-color: ${(props) => props.theme.colors?.background};
  border-radius: 0.25em;
  /* box-shadow removed for TV performance */
  cursor: pointer;
  direction: ${(props) => props.direction};
`;

// Generic Input Field
export const InputField = styled.input`
  padding: 10px;
  width: 100%;
  border-radius: 5px;
  font-size: 16px;
  background-color: ${(props) => props.theme.colors?.background};
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors?.background};
  }
`;

// Generic Input Field Name
export const InputFieldName = styled.div`
  width: 50%;
  text-align: ${({ direction }) => (direction === 'rtl' ? 'right' : 'left')};
  font-size: 16px;
  margin-right: ${({ direction }) => (direction === 'rtl' ? '0px' : '1px')};
`;

// Generic Container (replaces Container)
export const Container = styled.div`
  text-align: center;
  position: absolute;
  left: 0;
  right: 0;
  // top: 100px;
  // bottom: 100px;
  display: grid;
  overflow: hidden;
`;

// Generic Container For Modal (replaces ContainerForModal)
export const ContainerForModal = styled.div`
  display: block;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  grid-gap: 10px;
`;

// Generic Container Modal (replaces ContainerModal)
export const ContainerModal = styled.div`
  color: ${(props) => props.theme.colors?.buttonPrimary};
  overflow: hidden;
  display: inline-flex;
  justify-content: left;
  padding: 15px;
`;

// Generic Container Modal Internal (replaces ContainerModalInternal)
export const ContainerModalInternal = styled.div`
  -webkit-justify-content: center !important;
  color: ${(props) => props.theme.colors?.buttonPrimary};
  overflow: hidden;
  display: inline-flex;
  justify-content: left;
  padding: 20px;
`;

// Generic Back Arrow (replaces BackArrow)
export const BackArrow = styled.div`
  vertical-align: top;
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
  text-align: ${(props) => (props.isRTL ? 'right' : 'left')};
  position: relative;
  display: flex;
  align-items: center;
  font-size: 24px;
  overflow: hidden;
  z-index: 9999;
  cursor: pointer;
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV performance */
  
  &:hover {
    color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
    transform: scale(1.05); /* Reduced from 1.1 for TV performance */
  }
  
  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${(props) => props.theme.colors.glassBackground};
    backdrop-filter: ${(props) => props.theme.effects?.backdropBlurLight || 'blur(2px)'}; /* Reduced for TV performance */
    /* border: 1px solid ${(props) => props.theme.colors?.glassBorder || 'rgba(255, 255, 255, 0.2)'}; REMOVED for TV performance */
    transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV */
    
    &:hover {
      background: ${(props) => props.theme.colors?.buttonGradient || 'rgba(255, 255, 255, 0.2)'};
      /* box-shadow removed for TV performance */
    }
  }
`;

// Generic Container Welcome (replaces ContainerWelcome)
export const ContainerWelcome = styled.div`
  background-color: 'transparent';
  height: ${() => (window.screen.width <= 1280 ? '125px' : '200px')};
  width: ${() => (window.screen.width <= 1280 ? '300px' : '500px')};
  top: 50px;
  text-align: ${(props) => (props.lang_code === 'ar' ? 'right' : 'left')};
  border-radius: 10px;
  padding: 15px;
  margin: 10px;
`;

// Generic Container Latest News (replaces ContainerLatestNews)
export const ContainerLatestNews = styled.div`
  background-color: ${(props) => props.theme.colors?.background};
  top: 50px;
  text-align: ${(props) => (props.direction === 'rtl' ? 'center' : 'left')};
  border-radius: 10px;
  right: 0;
  width: ${() => (window.screen.width <= 1280 ? '300px' : '500px')};
  padding: 15px;
  margin: 10px;
`;

// Generic Container Notification (replaces ContainerNotification)
export const ContainerNotification = styled.div`
  background-color: ${(props) => props.theme.colors.glassBackground};
  color: ${(props) =>
    (typeof props.theme.colors === 'object' && props.theme.colors?.textPrimary) || '#000000'};
  direction: ltr;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 10px;
`;

//  Container Notification Modal (replaces ContainerNotificationModal)
export const ContainerNotificationModal = styled.div`
  padding: 5px 20px;
`;

//  Footer (replaces Footer from Footer.styled.js)
export const Footer = styled.footer`
  background: rgba(0, 0, 0, ${(props) => props.hidden ? 0 : 0.3});
  text-align: center;
  position: absolute;
  display: ${(props) => (props.hidden ? 'none' : 'inline-flex')};
  justify-content: center;
  left: 0;
  right: 0;
  bottom: 2.5%;
  margin: 5px;
  z-index: 2;
`;

// Baseline channels strip — above spinner; solid bar so items stay readable over video
export const ChannelsFooter = styled(Footer)`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0;
  padding: 12px 8px 28px;
  z-index: 200;
  display: ${(props) => (props.hidden ? 'none' : 'flex')};
  justify-content: center;
  align-items: flex-end;
  background: transparent;
  /* Avoid Tizen compositing veils from fullscreen siblings */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
`;

// Full-screen player layer for Baseline (video/native surface sits here).
export const ChannelPlayerStage = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  pointer-events: none;
`;

// Fills the player stage and centers the loading card (no transform — Tizen-safe).
export const ChannelLoadingBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
  box-sizing: border-box;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  justify-content: center;
  pointer-events: none;
`;

// Compact spinner card — centered by ChannelLoadingBackdrop, not viewport transform.
export const ChannelLoadingPanel = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-flex-direction: column;
  flex-direction: column;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  justify-content: center;
  padding: 32px 48px;
  border-radius: ${(props) => props.theme.effects?.borderRadiusMedium || '12px'};
  background: rgba(0, 0, 0, 0.85);
  pointer-events: none;
`;

export const ChannelLoadingTitle = styled.p`
  margin: 16px 0 0 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
  text-align: center;
`;

export const ChannelLoadingMessage = styled.p`
  margin: 8px 0 0 0;
  font-size: 1.1rem;
  font-weight: 500;
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
  text-align: center;
`;

//  Menu Modal (replaces MenuModal from Menu.styled.js)
export const MenuModal = styled.div`
  display: flex;
  margin: 0 auto;
  padding: 10px;
  height: auto;
  width: auto;
  align-items: flex-end;
  justify-content: center;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: fit-content;
  border-radius: ${(props) => props.theme.effects?.borderRadiusMedium || '12px'};
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV */
  will-change: transform;
`;

//  Menu Modal Item (replaces MenuModalItem from Menu.styled.js)
export const MenuModalItem = styled.div`
  border-radius: ${(props) => props.theme.effects?.borderRadiusMedium || '12px'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
  height: ${(props) => (props.active ? '220px' : '175px')};
  width: ${(props) => (props.active ? '220px' : '175px')};
  min-width: ${(props) => (props.active ? '220px' : '175px')};
  margin: 5px;
  cursor: pointer;
  background: ${(props) => (props.active 
    ? props.theme.colors?.buttonPrimary
    : props.theme.colors?.glassBackground || props.theme.colors?.buttonSecondary)} !important;
  backdrop-filter: ${(props) => props.theme.effects?.backdropBlurLight || 'blur(2px)'};
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'};
  /* box-shadow removed for TV performance */
  transform: ${(props) => (props.active ? 'scale(1.02) translateY(-2px)' : 'scale(1) translateY(0)')}; /* Reduced for TV performance */
  position: relative;
  z-index: ${(props) => (props.active ? '3' : '1')};
  overflow: hidden;
  will-change: transform, background, height, width, min-width;

  /* &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${(props) => (props.active 
      ? props.theme.colors?.glowGradient || 'linear-gradient(90deg, rgba(255, 255, 255, 0.6), transparent)'
      : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)')};
    border-radius: ${(props) => props.theme.effects?.borderRadiusMedium || '12px'} ${(props) => props.theme.effects?.borderRadiusMedium || '12px'} 0 0;
    transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV performance */
  } REMOVED for TV performance */

  /* &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) => props.theme.colors?.glassBackground || 'linear-gradient(135deg, rgba(255, 255, 255, 0.05), transparent)'};
    pointer-events: none;
    transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV performance */
  } REMOVED for TV performance */

  &:hover {
    /* border-color: ${(props) => props.theme.colors?.glassBorder || 'rgba(255, 255, 255, 0.3)'}; REMOVED for TV performance */
    transform: ${(props) => (props.active ? 'scale(1.03) translateY(-3px)' : 'scale(1.01) translateY(-1px)')}; /* Reduced for TV performance */
    /* box-shadow removed for TV performance */
    z-index: ${(props) => (props.active ? '4' : '2')};
  }

  &:active {
    transform: ${(props) => (props.active ? 'scale(1.01) translateY(-1px)' : 'scale(0.98) translateY(0)')}; /* Reduced for TV performance */
    /* box-shadow removed for TV performance */
    transition: ${(props) => props.theme.effects?.transitionFast || 'all 0.1s ease-out'}; /* Simplified for TV */
  }

  span {
    margin-top: 15px;
    font-size: 16px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    font-weight: 600;
    transition: ${(props) => props.theme.effects?.transitionFast || 'all 0.2s ease'};
    color: ${(props) => (props.active ? props.theme.colors?.textPrimary : props.theme.colors?.textPrimary)} !important;
    /* text-shadow removed for TV performance */
  }
`;

// Baseline channel tile — equal slots so active stays visually centered (no 175→220 reflow)
export const ChannelMenuItem = styled(MenuModalItem)`
  opacity: 1 !important;
  height: 175px !important;
  width: 175px !important;
  min-width: 175px !important;
  background: ${(props) =>
    props.active
      ? props.theme.colors?.buttonPrimary || '#273247'
      : '#3a3a3a'} !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  transform: ${(props) =>
    props.active ? 'scale(1.06) translateY(-2px)' : 'scale(1) translateY(0)'};
`;

//  Menu Modal Item Text (replaces MenuModalItemText from Menu.styled.js)
export const MenuModalItemText = styled.div`
  text-align: center;
  font-size: 1rem;
  font-weight: 600;
  width: 100%;
  color: #ffffff;
  /* text-shadow removed for TV performance */
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV performance */
  position: relative;
  z-index: 2;
  will-change: color;
`;

//  Menu Modal Item For Modal (replaces MenuModalItemForModal from Menu.styled.js)
export const MenuModalItemForModal = styled.div`
  background: ${(props) => props.theme.colors?.background};
  justify-content: center;
  align-items: center;
  display: grid;
  border-radius: 0.25em;
  padding: 10px;
  height: 150px;
  min-width: 220px;
  margin: 5px;
  z-index: 10;
`;

//  Menu Modal Header (replaces MenuModalHeader from Menu.styled.js)
export const MenuModalHeader = styled.div`
  background: ${(props) => props.theme.colors?.background};
  justify-content: center;
  align-items: center;
  display: grid;
  border-radius: 0.25em;
  padding: 10px;
  height: 100px;
  width: 100%;
  margin: 5px 0;
  z-index: 10;
  font-weight: bold;
`;

//  Menu Modal Row Item (replaces MenuModalRowItem from Menu.styled.js)
export const MenuModalRowItem = styled.div`
  background-color: ${(props) => props.theme.colors?.buttonSecondary};
  color: ${({ theme }) => theme.colors.textInactive};
  justify-content: center;
  align-items: center;
  display: grid;
  flex-direction: column;
  border-radius: 0.25em;
  padding: 10px;
  height: 100px;
  min-width: 220px;
  margin: 5px;
  z-index: 10;

  &.active {
    background-color: ${(props) => props.theme.colors?.buttonPrimary};
    color: ${(props) => props.theme.colors?.textActive};
  }
`;

//  Menu Modal Item Icon (replaces MenuModalItemIcon from Menu.styled.js)
export const MenuModalItemIcon = styled.div`
  text-align: center;
  font-size: ${(props) => (window.screen.width <= 1280 ? '50px' : '100px')};
`;

//  Menu Modal Item Icon For Modal (replaces MenuModalItemIconForModal from Menu.styled.js)
export const MenuModalItemIconForModal = styled.div`
  text-align: center;
  font-size: ${(props) => (window.screen.width <= 1280 ? '30px' : '70px')};
  float: left;
`;

//  Menu Modal Item Image (replaces MenuModalItemImage from Menu.styled.js)
export const MenuModalItemImage = styled.div`
  text-align: center;
  width: 100%;
  position: relative;
  z-index: 2;
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV performance */
  will-change: transform;
  
  img {
    border-radius: ${(props) => props.theme.effects?.borderRadiusSmall || '8px'};
                    /* box-shadow removed for TV performance */
                transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV performance */
                will-change: transform;
    
                      &:hover {
                    transform: scale(1.02); /* Reduced from 1.05 for TV performance */
                    /* box-shadow removed for TV performance */
                    transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'}; /* Simplified for TV performance */
                  }
  }
`;

//  Menu Modal Row Item Text (replaces MenuModalRowItemText from Menu.styled.js)
export const MenuModalRowItemText = styled.div`
  text-align: center;
`;

// Generic ImageBox (consolidated from blue/styles.js)
export const ImageBox = styled.div`
  border-radius: ${(props) => props.theme.effects?.borderRadiusLarge || '20px'};
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '200px'};
  margin: ${(props) => props.margin || '1rem 0'};
  background-size: cover !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
                  background: ${(props) => `url(${props.image}) no-repeat center center`};
                /* box-shadow removed for TV performance */
                position: relative;
                overflow: hidden;
                transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'};
                will-change: transform;

  /* &::before pseudo-element - REMOVED for TV performance */
  /* &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) => props.theme.colors?.glassBackground || 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), transparent)'};
    pointer-events: none;
    transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.3s ease'};
  } */

  &:hover {
    transform: scale(1.01); /* Reduced for TV performance */
    
    /* &::before hover effect - REMOVED for TV performance */
    /* &::before {
      background: ${(props) => props.theme.colors?.glowGradient || 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), transparent)'};
    } */
  }

                  &:active {
                  transform: scale(0.99); /* Reduced for TV performance */
                  /* box-shadow removed for TV performance */
                }
`;







 